'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Paper, 
  Title, 
  Table, 
  Button, 
  Text, 
  Group, 
  Badge, 
  Loader,
  ActionIcon
} from '@mantine/core';
import { IconTrash, IconRefresh } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { getUserSessions, terminateSession } from '../services/firestore';
import { UserSession, SessionManagerProps } from '../types';

export const SessionManager: React.FC<SessionManagerProps> = ({ 
  onTerminateSuccess, 
  onError 
}) => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  
  const loadSessions = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userSessions = await getUserSessions(user.uid);
      setSessions(userSessions);
      
      // Get current session ID
      const sessionId = localStorage.getItem('sessionId');
      setCurrentSessionId(sessionId);
    } catch (error) {
      console.error('Error loading sessions:', error);
      onError?.(error as Error);
    } finally {
      setLoading(false);
    }
  }, [user, onError]);
  
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);
  
  const handleTerminateSession = async (sessionId: string) => {
    try {
      await terminateSession(sessionId);
      setSessions(sessions.filter(session => session.id !== sessionId));
      onTerminateSuccess?.();
    } catch (error) {
      console.error('Error terminating session:', error);
      onError?.(error as Error);
    }
  };
  
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };
  
  const parseDeviceInfo = (deviceInfoStr: string) => {
    try {
      const deviceInfo = JSON.parse(deviceInfoStr);
      return {
        browser: getBrowserName(deviceInfo.userAgent),
        platform: deviceInfo.platform,
        language: deviceInfo.language
      };
    } catch (e) {
      return { browser: 'Unknown', platform: 'Unknown', language: 'Unknown' };
    }
  };
  
  const getBrowserName = (userAgent: string) => {
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'Internet Explorer';
    return 'Unknown';
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Paper radius="md" p="xl" withBorder>
      <Group justify="apart" mb="md">
        <Title order={2}>Active Sessions</Title>
        <Button 
          leftSection={<IconRefresh size={16} />} 
          variant="subtle"
          onClick={loadSessions}
        >
          Refresh
        </Button>
      </Group>
      
      {sessions.length === 0 ? (
        <Text color="dimmed">No active sessions found.</Text>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Device</th>
              <th>Last Active</th>
              <th>Started</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => {
              const deviceInfo = parseDeviceInfo(session.deviceInfo);
              const isCurrentSession = session.id === currentSessionId;
              
              return (
                <tr key={session.id}>
                  <td>
                    <Text fw={500}>{deviceInfo.browser}</Text>
                    <Text size="xs" color="dimmed">{deviceInfo.platform}</Text>
                  </td>
                  <td>{formatDate(session.lastActive)}</td>
                  <td>{formatDate(session.createdAt)}</td>
                  <td>
                    {isCurrentSession ? (
                      <Badge color="green">Current Session</Badge>
                    ) : (
                      <Badge color="blue">Active</Badge>
                    )}
                  </td>
                  <td>
                    {!isCurrentSession && (
                      <ActionIcon 
                        color="red" 
                        onClick={() => handleTerminateSession(session.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Paper>
  );
}; 